import React, { useState, useMemo } from 'react';
import {
  Film, Plus, Clock, Search, TrendingDown,
  Shield, CheckCircle2, AlertCircle, Loader2, ArrowRight
} from 'lucide-react';
import type { User, UserQuotaInfo, Job } from '@vidly/types';

interface DashboardViewProps {
  user: User;
  quota: UserQuotaInfo;
  jobs: Job[];
  onStartNewJob: () => void;
  onUpgradeClick: () => void;
  onSelectJob: (job: Job) => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  user,
  quota,
  jobs,
  onStartNewJob,
  onUpgradeClick,
  onSelectJob
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'processing' | 'review'>('all');

  const formatTime = (seconds?: number | null) => {
    if (!seconds) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const completedJobs = useMemo(() => jobs.filter(j => j.status === 'completed'), [jobs]);
  
  const totalSavedSeconds = useMemo(() => {
    return completedJobs.reduce((acc, j) => {
      const original = j.durationSeconds || 0;
      const target = j.targetDurationSeconds || 0;
      return acc + Math.max(0, original - target);
    }, 0);
  }, [completedJobs]);

  const formatSavedTime = (totalSecs: number) => {
    if (totalSecs < 60) return `${totalSecs}s`;
    const mins = Math.floor(totalSecs / 60);
    if (mins < 60) return `${mins} min`;
    const hours = (mins / 60).toFixed(1);
    return `${hours} h`;
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const filename = job.settings?.originalFilename || job.id;
      const matchesSearch = filename.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;

      if (statusFilter === 'all') return true;
      if (statusFilter === 'completed') return job.status === 'completed';
      if (statusFilter === 'processing') return ['uploading', 'validating', 'transcribing', 'planning', 'rendering'].includes(job.status);
      if (statusFilter === 'review') return job.status === 'review_pending';
      return true;
    });
  }, [jobs, searchQuery, statusFilter]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700 text-[11px] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            Completado
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700 text-[11px] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-500"></span>
            Error
          </span>
        );
      case 'review_pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700 text-[11px] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-400"></span>
            Revisión
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-300 text-[11px] font-medium">
            <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />
            En proceso
          </span>
        );
    }
  };

  return (
    <div className="space-y-6 text-left max-w-5xl mx-auto">
      
      {/* Top Welcome / Header */}
      <div className="p-6 rounded-2xl border border-zinc-800 bg-zinc-900/30 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1.5">
            <h2 className="text-2xl font-bold text-white tracking-tight">
              Hola, {user.name || user.email.split('@')[0]}
            </h2>
            <span className="px-2.5 py-0.5 rounded text-xs font-semibold bg-zinc-800 text-zinc-300 uppercase tracking-wide">
              {user.plan}
            </span>
          </div>
          <p className="text-sm text-zinc-400">
            Gestiona tus vídeos procesados o inicia un nuevo resumen inteligente.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <div className="px-3 py-1.5 rounded-lg bg-zinc-900 border border-zinc-800 text-xs">
            <span className="text-zinc-500 mr-1.5">Cuota:</span>
            <span className="font-medium text-white">
              {quota.videosRemainingToday} de {quota.maxDailyVideos ?? quota.maxDailyJobs ?? 1} libres
            </span>
          </div>

          <button
            onClick={onStartNewJob}
            disabled={quota.videosRemainingToday <= 0}
            className="btn-primary py-2 px-4 text-xs font-medium flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Nuevo resumen</span>
          </button>
        </div>
      </div>

      {/* 3 Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/20 space-y-1">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <TrendingDown className="w-3.5 h-3.5 text-zinc-400" />
            <span>Tiempo ahorrado</span>
          </div>
          <p className="text-2xl font-semibold text-white">{formatSavedTime(totalSavedSeconds)}</p>
          <p className="text-[11px] text-zinc-500">En vídeos condensados</p>
        </div>

        <div className="p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/20 space-y-1">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Film className="w-3.5 h-3.5 text-zinc-400" />
            <span>Vídeos procesados</span>
          </div>
          <p className="text-2xl font-semibold text-white">{completedJobs.length}</p>
          <p className="text-[11px] text-zinc-500">Completados con éxito</p>
        </div>

        <div className="p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/20 space-y-1">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Shield className="w-3.5 h-3.5 text-zinc-400" />
            <span>Retención en R2</span>
          </div>
          <p className="text-2xl font-semibold text-white">{quota.historyRetentionDays} días</p>
          <p className="text-[11px] text-zinc-500">
            {user.plan === 'premium' ? 'Plan Premium (30 días)' : 'Purga en 24h'}
          </p>
        </div>
      </div>

      {/* Quota Exhausted Alert */}
      {quota.videosRemainingToday <= 0 && user.plan === 'free' && (
        <div className="p-3.5 rounded-xl border border-zinc-700 bg-zinc-900/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <span className="text-zinc-300">
            Has consumido tu vídeo diario del plan Free. Tu crédito se restablece a las 00:00 UTC.
          </span>
          <button
            onClick={onUpgradeClick}
            className="btn-primary py-1.5 px-3 text-xs shrink-0"
          >
            Pasar a Premium (5 al día)
          </button>
        </div>
      )}

      {/* Jobs History Container */}
      <div className="p-5 rounded-2xl border border-zinc-800 bg-zinc-900/20 space-y-4">
        
        {/* Search & Filter Bar */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-white flex items-center gap-2">
              <span>Historial de trabajos</span>
              <span className="text-xs text-zinc-500">({filteredJobs.length})</span>
            </h3>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-8 pr-3 py-1.5 rounded-lg bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-700 w-36 sm:w-44"
              />
            </div>
          </div>

          {/* Status filters — scrollable on mobile */}
          <div className="flex items-center gap-1 bg-zinc-950 p-1 rounded-lg border border-zinc-800 text-xs overflow-x-auto">
            {(
              [
                { id: 'all', label: 'Todos' },
                { id: 'completed', label: 'Completados' },
                { id: 'review', label: 'Revisión' },
                { id: 'processing', label: 'En proceso' }
              ] as const
            ).map(tab => (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`px-2.5 py-1 rounded-md transition-colors whitespace-nowrap ${
                  statusFilter === tab.id
                    ? 'bg-zinc-800 text-white font-medium'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Jobs List */}
        {filteredJobs.length === 0 ? (
          <div className="py-10 text-center text-xs text-zinc-500">
            {searchQuery
              ? `No hay vídeos que coincidan con "${searchQuery}".`
              : 'No hay trabajos en este filtro. Sube tu primer vídeo para empezar.'}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredJobs.map((job) => {
              const originalSec = job.durationSeconds || 0;
              const targetSec = job.targetDurationSeconds || 0;
              const reductionPercent = originalSec > 0 && targetSec > 0
                ? Math.round((1 - targetSec / originalSec) * 100)
                : 0;

              return (
                <div
                  key={job.id}
                  onClick={() => onSelectJob(job)}
                  className="p-3.5 rounded-xl border border-zinc-800/80 bg-zinc-950/60 hover:border-zinc-700/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer transition-all group"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2.5">
                      <h4 className="text-xs font-medium text-zinc-200 group-hover:text-white transition-colors">
                        {job.settings?.originalFilename || `Trabajo ${job.id.substring(0, 8)}`}
                      </h4>
                      {getStatusBadge(job.status)}
                      {reductionPercent > 0 && job.status === 'completed' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                          -{reductionPercent}%
                        </span>
                      )}
                    </div>

                    <p className="text-[11px] text-zinc-500 flex items-center gap-2">
                      <span>Original: {formatTime(originalSec)}</span>
                      <span>→</span>
                      <span className="text-zinc-300 font-medium">Resumen: {formatTime(targetSec)}</span>
                      <span>•</span>
                      <span>{new Date(job.createdAt).toLocaleDateString('es-ES', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </p>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-zinc-400 group-hover:text-white transition-colors">
                    <span>Abrir</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

    </div>
  );
};
